import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { TicketErrorModal } from './TicketErrorModal'
import {
  placeTicketsIntoCart,
  processFreeOrder,
  isWaitingRoomRequiredError,
} from '../../../../../utilities/api'
import { redirectToWaitingRoom } from '../../../../../utilities/waitingRoom'
import { useNavigate } from 'react-router-dom'
import authService from '../../../../../utilities/services/auth.service'
import { trackAddToCart } from '../../../../../utilities/tracking'
import { storeCartSession } from '../../../../../utilities/cartSessionTracking'
import { readTrackingCode } from '../../../../../utilities/trackingLink'
import useSeatmapStore from '../../../../../stores/seatmapStore'
import { cartTicketGroups } from '../../../../../utilities/listingSelection'
import useFiltersStore from '../../../../../stores/filtersStore'

const navigateToComplimentarySuccess = (
  navigate,
  processRes,
  clearCheckingAvailability
) => {
  clearCheckingAvailability()
  sessionStorage.setItem('order', JSON.stringify({ id: processRes.data?.id }))
  navigate(
    `/checkout/checkout-success?intentId=${encodeURIComponent(processRes.data?.paymentIntentId)}`
  )
}

// Create the context
const CheckoutContext = createContext(undefined)

export const useCheckout = () => {
  const context = useContext(CheckoutContext)
  if (!context) {
    throw new Error('Context must be used within a CheckoutProvider')
  }
  return context
}

// Provide all functionality + modal for proceeding to checkout
export const CheckoutProvider = ({ children }) => {
  // Zustand
  const seatedError = useSeatmapStore((state) => state.seatedError)
  const setSeatedError = useSeatmapStore((state) => state.setSeatedError)
  const selectedFromMap = useSeatmapStore((state) => state.selectedFromMap)
  const event = useFiltersStore((state) => state.event)

  const dialogRef = useRef(null)
  const checkoutInFlightRef = useRef(false)
  const navigate = useNavigate()
  const [checkingAvailability, setCheckingAvailability] = useState(false)

  useEffect(() => {
    if (seatedError) {
      dialogRef.current.showModal()
    }
  }, [seatedError])

  // This is only valid for a single ticket
  const checkout = (isQuickpick, selectedFromQuickpicks) => {
    if (checkoutInFlightRef.current || checkingAvailability) return
    checkoutInFlightRef.current = true
    setCheckingAvailability(true)
    const clearCheckingAvailability = () => {
      checkoutInFlightRef.current = false
      setCheckingAvailability(false)
    }
    const selectedGroups = isQuickpick
      ? [selectedFromQuickpicks]
      : [...selectedFromMap]
    let data = {
      ticketGroups: cartTicketGroups(selectedGroups),
      eventId: event.id,
    }

    placeTicketsIntoCart(data)
      .then((res) => {
        trackAddToCart({
          organization: event?.organization,
          cart: res.data,
        })
        storeCartSession({
          cartId: res.data.cartId,
          sessionId: res.data.sessionId,
        })
        sessionStorage.setItem(
          'cart',
          JSON.stringify({ cartId: res.data.cartId })
        )
        if (res.data.freeOffer) {
          if (!authService.isLoggedIn()) {
            clearCheckingAvailability()
            navigate('/checkout')
            return
          }
          processFreeOrder({
            cartId: res.data.cartId,
            trackingCode: readTrackingCode(event?.uuid),
          })
            .then((processRes) =>
              navigateToComplimentarySuccess(
                navigate,
                processRes,
                clearCheckingAvailability
              )
            )
            .catch((err) => {
              clearCheckingAvailability()
              if (err.response) {
                setSeatedError({
                  status: err.response.status,
                  title: 'Checkout unavailable',
                  message:
                    err.response.data?.error?.message ||
                    'Unable to complete complimentary checkout.',
                  buttonText: 'Return to tickets list',
                })
              } else {
                setSeatedError({
                  status: 500,
                  title: 'Checkout unavailable',
                  message: 'Network error occurred',
                  buttonText: 'Return to tickets list',
                })
              }
            })
          return
        }
        clearCheckingAvailability()
        navigate(`/checkout`)
      })
      .catch((err) => {
        clearCheckingAvailability()
        if (isWaitingRoomRequiredError(err) && event?.uuid) {
          redirectToWaitingRoom(
            navigate,
            event.seoUrl,
            event.shortCode,
            event.uuid,
            'seated'
          )
          return
        }
        if (err.response) {
          setSeatedError({
            status: err.response.status,
            title: 'Selected tickets not available',
            message: err.response.data.error.message || 'An error occurred',
            buttonText: 'Return to tickets list',
          })
        } else {
          setSeatedError({
            status: 500,
            title: 'Selected tickets not available',
            message: 'Network error occurred',
            buttonText: 'Return to tickets list',
          })
        }
      })
  }

  const resetSeatedError = () => {
    setSeatedError(null)
  }

  const modalClose = () => {
    resetSeatedError()
    dialogRef.current.close()
    // Probably just want to refresh here if tickets not available
  }

  return (
    <CheckoutContext.Provider
      value={{
        checkout,
        checkingAvailability,
        event,
      }}
    >
      {children}
      <TicketErrorModal
        dialogRef={dialogRef}
        seatedError={seatedError}
        handleClose={modalClose}
      />
    </CheckoutContext.Provider>
  )
}
